import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Compass, BookOpen, Lightbulb, Calendar, Sparkles, 
  ArrowRight, Save, Plus, Loader2, Lock, CheckCircle,
  ChevronRight, BookMarked, Heart, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MODES = [
  {
    id: 'chronological',
    name: 'Chronological',
    icon: BookOpen,
    description: 'Follow the biblical narrative verse-by-verse',
    color: 'bg-blue-500',
    tiers: ['pro', 'studio', 'church_teams']
  },
  {
    id: 'theological',
    name: 'Theological',
    icon: Lightbulb,
    description: 'Connect doctrines in logical progression',
    color: 'bg-purple-500',
    tiers: ['pro', 'studio', 'church_teams']
  },
  {
    id: 'topical',
    name: 'Topical',
    icon: Compass,
    description: 'Build thematic arcs with practical applications',
    color: 'bg-green-500',
    tiers: ['trial', 'starter', 'pro', 'studio', 'church_teams']
  },
  {
    id: 'seasonal',
    name: 'Seasonal',
    icon: Calendar,
    description: 'Align with church calendar and ministry rhythm',
    color: 'bg-orange-500',
    tiers: ['studio', 'church_teams']
  },
  {
    id: 'custom',
    name: 'Custom Flow',
    icon: Sparkles,
    description: 'Define your own destination for the series',
    color: 'bg-pink-500',
    tiers: ['pro', 'studio', 'church_teams']
  }
];

export default function NextStepNavigator({ 
  isOpen, 
  onClose, 
  sermon = null, 
  series = null,
  user,
  onSermonCreated 
}) {
  const [step, setStep] = useState('input'); // input, mode, loading, results
  const [inputType, setInputType] = useState(sermon ? 'sermon' : series ? 'series' : 'manual');
  const [manualInput, setManualInput] = useState({
    passage: '',
    theme: '',
    doctrine: '',
    topic: ''
  });
  const [selectedMode, setSelectedMode] = useState(null);
  const [customDirection, setCustomDirection] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [generatingSermonId, setGeneratingSermonId] = useState(null);

  const userTier = user?.subscription_tier?.toLowerCase() || 'starter';

  const canAccessMode = (mode) => {
    return mode.tiers.includes(userTier);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleGenerateSuggestions = async () => {
    if (!selectedMode) {
      toast.error('Please select a mode');
      return;
    }

    if (selectedMode === 'custom' && !customDirection.trim()) {
      toast.error('Please describe your custom direction');
      return;
    }

    if (inputType === 'manual' && !manualInput.passage && !manualInput.theme && !manualInput.topic) {
      toast.error('Please enter at least one field (passage, theme, or topic)');
      return;
    }

    setLoading(true);
    setStep('loading');

    try {
      const response = await axios.post(`${API}/nextstep/generate`, {
        mode: selectedMode,
        input_type: inputType,
        sermon_id: sermon?.id,
        series_id: series?.id,
        manual_input: inputType === 'manual' ? manualInput : null,
        custom_direction: selectedMode === 'custom' ? customDirection : null
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      setSuggestions(response.data.suggestions || []);
      setStep('results');
    } catch (error) {
      console.error('NextStep error:', error);
      const message = error.response?.data?.detail || 'Failed to generate suggestions';
      toast.error(message);
      setStep('mode');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSuggestion = async (suggestion) => {
    setSavingId(suggestion.id);
    try {
      await axios.post(`${API}/nextstep/save`, null, {
        params: {
          source_sermon_id: sermon?.id,
          source_series_id: series?.id
        },
        data: suggestion,
        headers: { 
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      toast.success('Suggestion saved to your library!');
    } catch (error) {
      toast.error('Failed to save suggestion');
    } finally {
      setSavingId(null);
    }
  };

  const handleGenerateSermon = async (suggestion) => {
    setGeneratingSermonId(suggestion.id);
    try {
      const response = await axios.post(`${API}/nextstep/to-sermon`, suggestion, {
        headers: { 
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      toast.success('Sermon created successfully!');
      onSermonCreated?.(response.data.sermon);
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate sermon';
      toast.error(message);
    } finally {
      setGeneratingSermonId(null);
    }
  };

  const resetNavigator = () => {
    setStep('input');
    setSelectedMode(null);
    setSuggestions([]);
    setCustomDirection('');
  };

  useEffect(() => {
    if (isOpen) {
      resetNavigator();
      // Set the appropriate input type based on what's available
      if (sermon) {
        setInputType('sermon');
      } else if (series) {
        setInputType('series');
      } else {
        setInputType('manual');
      }
    }
  }, [isOpen, sermon, series]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl text-kingdom-blue">
            <Compass className="w-7 h-7 text-glory-500" />
            NextStep Navigator™
          </DialogTitle>
          <DialogDescription className="text-base">
            Never get stuck again — discover where your sermon or series should go next.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Input Selection */}
        {step === 'input' && (
          <div className="space-y-6 py-4">
            <div>
              <h3 className="font-semibold text-lg mb-3 text-kingdom-blue">What would you like to continue?</h3>
              <Tabs value={inputType} onValueChange={setInputType} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="sermon" disabled={!sermon}>
                    <BookMarked className="w-4 h-4 mr-2" />
                    Current Sermon
                  </TabsTrigger>
                  <TabsTrigger value="series" disabled={!series}>
                    <BookOpen className="w-4 h-4 mr-2" />
                    Current Series
                  </TabsTrigger>
                  <TabsTrigger value="manual">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Enter Manually
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="sermon" className="mt-4">
                  {sermon ? (
                    <Card className="border-glory-300 bg-white shadow-sm">
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-lg text-gray-900">{sermon.title}</h4>
                        <p className="text-sm text-gray-700 mt-2">
                          {sermon.scripture_reference && `📖 ${sermon.scripture_reference}`}
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          {sermon.topic && `🎯 Theme: ${sermon.topic}`}
                        </p>
                        {sermon.content && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                            {sermon.content.substring(0, 150)}...
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="border-gray-300 bg-gray-100">
                      <CardContent className="p-6 text-center">
                        <Compass className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                        <p className="text-gray-700 font-medium">No sermon selected</p>
                        <p className="text-gray-500 text-sm mt-1">Use the compass button on a sermon card or switch to "Enter Manually"</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="series" className="mt-4">
                  {series ? (
                    <Card className="border-glory-300 bg-white shadow-sm">
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-lg text-gray-900">{series.title}</h4>
                        <p className="text-sm text-gray-700 mt-2">
                          {series.theme && `🎯 Theme: ${series.theme}`}
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          📅 {series.weeks} week series
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="border-gray-300 bg-gray-100">
                      <CardContent className="p-6 text-center">
                        <BookOpen className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                        <p className="text-gray-700 font-medium">No series selected</p>
                        <p className="text-gray-500 text-sm mt-1">Enter details manually to continue</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="manual" className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="passage">Scripture Passage</Label>
                      <Input
                        id="passage"
                        placeholder="e.g., Acts 2:1-13"
                        value={manualInput.passage}
                        onChange={(e) => setManualInput({ ...manualInput, passage: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="theme">Theme</Label>
                      <Input
                        id="theme"
                        placeholder="e.g., Faith, Identity in Christ"
                        value={manualInput.theme}
                        onChange={(e) => setManualInput({ ...manualInput, theme: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="doctrine">Doctrine</Label>
                      <Input
                        id="doctrine"
                        placeholder="e.g., Pneumatology, Ecclesiology"
                        value={manualInput.doctrine}
                        onChange={(e) => setManualInput({ ...manualInput, doctrine: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="topic">Topic</Label>
                      <Input
                        id="topic"
                        placeholder="e.g., Prayer, Leadership"
                        value={manualInput.topic}
                        onChange={(e) => setManualInput({ ...manualInput, topic: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <Button 
              onClick={() => {
                // Validate input based on type
                if (inputType === 'sermon' && !sermon) {
                  toast.error('Please select a sermon or switch to "Enter Manually"');
                  return;
                }
                if (inputType === 'series' && !series) {
                  toast.error('Please select a series or switch to "Enter Manually"');
                  return;
                }
                if (inputType === 'manual' && !manualInput.passage && !manualInput.theme && !manualInput.topic && !manualInput.doctrine) {
                  toast.error('Please enter at least one field (passage, theme, doctrine, or topic)');
                  return;
                }
                setStep('mode');
              }} 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 text-base"
            >
              Continue
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Mode Selection */}
        {step === 'mode' && (
          <div className="space-y-6 py-4">
            <div>
              <h3 className="font-semibold text-lg mb-1 text-kingdom-blue">
                How would you like to continue the journey?
              </h3>
              <p className="text-sm text-gray-500 mb-4">Select a navigation mode</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {MODES.map((mode) => {
                const Icon = mode.icon;
                const isLocked = !canAccessMode(mode);
                const isSelected = selectedMode === mode.id;

                return (
                  <Card
                    key={mode.id}
                    className={`cursor-pointer transition-all ${
                      isLocked 
                        ? 'opacity-60 cursor-not-allowed' 
                        : isSelected 
                          ? 'ring-2 ring-glory-500 border-glory-500' 
                          : 'hover:border-glory-300 hover:shadow-md'
                    }`}
                    onClick={() => !isLocked && setSelectedMode(mode.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${mode.color} text-white`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-kingdom-blue">{mode.name}</h4>
                            {isLocked && <Lock className="w-4 h-4 text-gray-400" />}
                            {isSelected && <CheckCircle className="w-4 h-4 text-glory-500" />}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{mode.description}</p>
                          {isLocked && (
                            <Badge variant="outline" className="mt-2 text-xs">
                              {mode.tiers[0] === 'studio' ? 'Studio+' : 'Pro+'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {selectedMode === 'custom' && (
              <div className="mt-4">
                <Label htmlFor="customDirection">Describe your custom direction</Label>
                <Textarea
                  id="customDirection"
                  placeholder="e.g., I want this series to lead into Pentecost... or I want to end with a revival service..."
                  value={customDirection}
                  onChange={(e) => setCustomDirection(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('input')} className="flex-1 text-gray-700 font-medium">
                Back
              </Button>
              <Button 
                onClick={handleGenerateSuggestions} 
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                disabled={!selectedMode}
              >
                <Compass className="w-4 h-4 mr-2" />
                What's Next?
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Loading */}
        {step === 'loading' && (
          <div className="py-16 text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-glory-500" />
            <h3 className="mt-4 text-lg font-semibold text-kingdom-blue">
              Discovering Your Next Step...
            </h3>
            <p className="text-gray-500 mt-2">
              Analyzing context and generating Spirit-sensitive suggestions
            </p>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 'results' && (
          <div className="space-y-6 py-4">
            <div>
              <h3 className="font-semibold text-lg mb-1 text-kingdom-blue">
                Your NextStep Recommendations
              </h3>
              <p className="text-sm text-gray-500">
                Choose a path forward or save for later
              </p>
            </div>

            <div className="space-y-4">
              {suggestions.map((suggestion, index) => (
                <Card 
                  key={suggestion.id} 
                  className="border-l-4 hover:shadow-lg transition-shadow"
                  style={{ 
                    borderLeftColor: index === 0 ? '#1E3A5F' : index === 1 ? '#E5C163' : '#6B7280' 
                  }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge 
                          className={`mb-2 ${
                            index === 0 ? 'bg-kingdom-blue' : 
                            index === 1 ? 'bg-glory-500' : 
                            'bg-gray-500'
                          }`}
                        >
                          Option {suggestion.option_letter}
                        </Badge>
                        <CardTitle className="text-xl text-kingdom-blue">
                          {suggestion.title}
                        </CardTitle>
                        {suggestion.passage && (
                          <CardDescription className="mt-1">
                            📖 {suggestion.passage}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {suggestion.theme && (
                      <div className="flex items-center gap-2 text-sm">
                        <Heart className="w-4 h-4 text-glory-500" />
                        <span className="font-medium">Theme:</span>
                        <span className="text-gray-600">{suggestion.theme}</span>
                      </div>
                    )}

                    <div className="bg-glory-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-700 italic">
                        "{suggestion.why_next}"
                      </p>
                    </div>

                    <div>
                      <h5 className="font-semibold text-sm text-kingdom-blue mb-1">Big Idea</h5>
                      <p className="text-sm text-gray-600">{suggestion.big_idea}</p>
                    </div>

                    {suggestion.outline_preview && suggestion.outline_preview.length > 0 && (
                      <div>
                        <h5 className="font-semibold text-sm text-kingdom-blue mb-1">
                          Outline Preview
                        </h5>
                        <ul className="text-sm text-gray-600 list-disc list-inside">
                          {suggestion.outline_preview.map((point, i) => (
                            <li key={i}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {suggestion.continuity_note && (
                      <div className="text-sm text-gray-500 flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 mt-0.5 text-glory-500" />
                        <span>{suggestion.continuity_note}</span>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSaveSuggestion(suggestion)}
                        disabled={savingId === suggestion.id}
                        className="flex-1"
                      >
                        {savingId === suggestion.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Save for Later
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleGenerateSermon(suggestion)}
                        disabled={generatingSermonId === suggestion.id}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                      >
                        {generatingSermonId === suggestion.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        Generate Sermon
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={resetNavigator} className="flex-1 text-gray-700 font-medium">
                Start Over
              </Button>
              <Button variant="outline" onClick={onClose} className="flex-1 text-gray-700 font-medium">
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
